/**
 * @fileoverview
 * API 레이어 전용 토큰 리프레시 유틸리티
 *
 * NOTE: 레이어 격리 원칙에 따라 이 파일은 API 레이어 전용입니다.
 * App 레이어(`layers/apps/curator/src/middleware/auth/refreshToken.ts`)와 유사하지만,
 * 레이어 간 직접 참조를 방지하기 위해 독립적으로 유지됩니다.
 * 두 레이어 간 일관성은 유지하되, 각 레이어의 요구사항에 맞게 구현됩니다.
 */

import { createCookieStore } from './cookieUtils';
import type { RefreshTokenResponse } from './types';

const ACCESS_TOKEN_KEY = 'access_token';
const COOKIE_DOMAIN = '.29cm.co.kr';
const USER_AUTH_API_URL = process.env.NEXT_PUBLIC_USER_AUTH_API_URL;
const TOKEN_REFRESH_API_URL = `${USER_AUTH_API_URL}/api/v1/auth/refresh`;

/**
 * 클라이언트용 토큰 리프레시
 *
 * 리프레시 API를 호출하고 응답받은 토큰을 document.cookie에 설정합니다.
 * 호출 전 x-device-id 존재 여부를 체크하며, 없으면 throw → 인터셉터 catch에서 onRefreshFail(로그아웃 처리) 호출.
 *
 * @throws x-device-id 없음 시 Error('x-device-id required for refresh')
 * @throws 리프레시 실패 시 Error('Token refresh failed')
 *
 * @example
 * ```typescript
 * await refreshAccessTokenClient();
 * // document.cookie에 새 토큰이 설정됨
 * ```
 */
export const refreshAccessTokenClient = async (): Promise<void> => {
  const cookieStore = createCookieStore({
    domain: COOKIE_DOMAIN,
    path: '/',
    secure: true,
    sameSite: 'Lax',
  });

  const deviceId = cookieStore.get('x-device-id');
  if (!deviceId) {
    throw new Error('x-device-id required for refresh');
  }

  const response = await fetch(TOKEN_REFRESH_API_URL, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-device-id': deviceId,
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const result = (await response.json()) as RefreshTokenResponse;
  const { accessToken, expiresIn } = result.data;

  if (expiresIn > 0) {
    cookieStore.set(ACCESS_TOKEN_KEY, accessToken, {
      maxAge: expiresIn,
    });
  }
};
